import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "next/router";

import useErrorConsumer from "@kikoff/hooks/src/useErrorConsumer";
import { web } from "@kikoff/proto/src/protos";

import { PageRegistration } from "@component/flows/hooks/usePageFlow";
import {
  fetchPaymentMethods,
  selectHasPaymentMethod,
  selectPaymentMethodBy,
} from "@feature/funds";
import { selectFeatureFlag } from "@feature/page";
import { updateOrders } from "@feature/shopping";
import { useBackupPaymentMethodNudgeEntry } from "@feature/user";
import OrderConfirmationView from "@page/dashboard/store/order/[token]";
import { useOverlaysController } from "@src/overlay";
import { l10nResources } from "@util/l10n";

const l10n = l10nResources({
  en: `order-confirmation-setup-repayment = Set up repayment
order-confirmation-go-to-dashboard = Continue
`,
  es: `order-confirmation-setup-repayment = Configurar reembolso
order-confirmation-go-to-dashboard = Seguir
`,
});

interface OrderConfirmationViewProps extends PageRegistration {
  activating?: boolean;
}

function OnboardingOrderConfirmationView({
  activating,
  Button,
  configFlow,
}: OrderConfirmationViewProps) {
  const dispatch = useDispatch();
  const error = useErrorConsumer();
  const overlays = useOverlaysController();

  const orders = useSelector((state) => state.shopping.orders);
  const paymentMethodToken = useSelector(
    (state) => state.creditLine.account.paymentMethodToken
  );
  const paymentMethodType = useSelector(
    selectPaymentMethodBy("token", paymentMethodToken)
  )?.stripeDetails?.fundingType;
  const isPrepaidCard =
    paymentMethodType === web.public_.StripeDetails.FundingType.PREPAID;
  const displayableOrders = (orders || []).filter(
    (userOrder) => userOrder.hideOrder === false
  );
  const hasPaymentMethod = useSelector(selectHasPaymentMethod());
  const showPersonalizedOboarding = useSelector(
    selectFeatureFlag("personalized_credit_goal_onboarding")
  );
  const hdyhauOnboardingSurvey = useSelector(
    selectFeatureFlag("hdyhau_onboarding_experiment")
  );

  const backupPaymentMethodNudgeEntry = useBackupPaymentMethodNudgeEntry();

  useEffect(() => {
    dispatch(updateOrders()).catch((err: Error) => {
      error.throw(err.message);
    });

    dispatch(fetchPaymentMethods()).catch((err: Error) => {
      error.throw(err.message);
    });
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (displayableOrders?.length > 0) {
      const orderToken = displayableOrders[0].token;
      url.searchParams.set("order", orderToken);
      Router.replace(url);
    }
  }, [orders]);

  configFlow.button(
    <Button
      onClick={async (e) => {
        if (!hasPaymentMethod) {
          return;
        }

        // FIXME: remove preventDefault because it prevents the other views
        //   after this from being shown. This is why mobile-app view was
        //   being skipped.
        //
        // Prevents PageFlow default behaviour (going to next
        // view) since e.defaultPrevented is checked there
        if (isPrepaidCard && backupPaymentMethodNudgeEntry) {
          await overlays.push("backup_payment_nudge/add_backup_payment");
        }
        if (hdyhauOnboardingSurvey) {
          e.preventDefault();
          Router.replace("/dashboard/store/onboarding-survey");
        }
        if (showPersonalizedOboarding) {
          e.preventDefault();
          Router.replace("/dashboard/goals");
        }
      }}
    >
      {l10n.getString(
        hasPaymentMethod
          ? "order-confirmation-go-to-dashboard"
          : "order-confirmation-setup-repayment"
      )}
    </Button>,
    [hasPaymentMethod, orders, showPersonalizedOboarding]
  );

  return <OrderConfirmationView activating={activating} />;
}

export default OnboardingOrderConfirmationView;
